<script setup lang="ts">
import { ref, onMounted } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  Item,
  ItemBatch,
  ItemSerial,
  Vendor,
  Warehouse,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDF,
  formatDate,
  isEmpty,
  printPDF,
  sharePDF,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";
import { useScreenSize } from "../../../../utils/useScreenSize";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import IconComponent from "../../../../components/ui/common/IconComponent.vue";
import TabComponent from "../../../../components/ui/form/TabComponent.vue";
import ItemPurchaseGrid from "../../../../components/ui/Templates/ItemPurchaseGrid.vue";
import AccountingLog from "../../../../components/ui/Templates/AccountingLog.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import { tokenManager } from "../../../../api/utils/tokenManager";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, routePath: string, title: string): void;
}>();

const { isMobile } = useScreenSize();
const isReadOnly = ref<boolean>(props.mode === "view");

const { fields, formData, setFieldRules, validateForm, setFormData, resetForm } = useFormValidation({
  purchase_orders_id: props.data?.purchase_orders_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  vendor: props.data?.vendor,
  vendors_id: props.data?.vendors_id || "",
  currency: props.data?.currency || tokenManager.getSetting("default_currency", true) || "MVR",
  exchange_rate: props.data?.exchange_rate || 1,
  currencys_id: props.data?.currencys_id || 1,
  prefix: props.data?.prefix || "",
  ref_no: props.data?.ref_no || "",
  warehouses_id: props.data?.warehouses_id || "",
  price_lists_id: props.data?.price_lists_id || "",
  credit_terms_id: props.data?.credit_terms_id || "",
  vendor_reference: props.data?.vendor_reference || "",
  remarks: props.data?.remarks || "",
  status_id: props.data?.status_id || "",
  uid: props.data?.uid || "",
  items: props.data?.items || ([] as Item[]),
  action: "",
  accounting: [],
  invoice: null as any,
});

const vendor = ref<Vendor>();
const warehouse = ref<Warehouse>();

setFieldRules("vendors_id", [required("Select Vendor")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
setFieldRules("price_lists_id", [required("Select Price List")]);

const handleWarehouse = (data: any) => {
  warehouse.value = data;
  prepareItemRow();
};

const setData = (data: any) => {
  setFormData(data);
  fields.value.date_time.value = formatDate(data.date_time);
  isReadOnly.value = true
};

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setData(response.data);
  }
};

const validateGrid = async (): Promise<boolean> => {
  for (const item of formData.value.items) {
    if (item.tracked_by === "Batch") {
      const batch_qty = item.batches?.reduce(
        (sum: number, batch: ItemBatch) => {
          if (
            typeof batch.quantity === "number" &&
            batch.batch_no &&
            (!item.is_expiry || batch.expiry_date) &&
            (!warehouse.value?.shelf_managed || batch.warehouse_shelfs_id)
          ) {
            return sum + batch.quantity;
          }
          console.log(sum + " == > " + item.inventory_qty);
          return sum;
        },
        0
      );
      if (batch_qty !== item.inventory_qty) {
        return false;
      }
    } else if (item.tracked_by === "Serial") {
      const serial_qty = item.serials?.reduce(
        (sum: number, serial: ItemSerial) => {
          if (
            typeof serial.quantity === "number" &&
            serial.serial_no &&
            (!item.is_expiry || serial.expiry_date) &&
            (!warehouse.value?.shelf_managed || serial.warehouse_shelfs_id)
          ) {
            return sum + serial.quantity;
          }
          return sum;
        },
        0
      );
      if (serial_qty !== item.inventory_qty) {
        return false;
      }
    }
  }
  return true;
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    const isValid = await validateGrid();
    if (!isValid) {
      MessageBox.show(
        "There are some Batch, Serial, Shelf or Variant Quantity differences",
        "warning"
      );
      return;
    }
    let items = formData.value.items.filter((e: any) => e.items_id != "");
    if (items.length <= 0) {
      MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    formData.value.action =
      download || share || print ? "download" : mail ? "mail" : "";
    //formData.value.download = download || share || print || mail
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show(props.title + " Saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDF(response.data.file);
      } else if (mail) {
        Toast.show(props.title + " mailed successfully");
      } else if (share) {
        sharePDF(
          response.data.file,
          vendor.value ? vendor.value.name : props.title + " from Faseyha POS",
          "viber"
        );
      } else if (print) {
        printPDF(response.data.file)
      } else {
        setData(response.data);
      }
    }
  }
};

const clearForm = () => {
  resetForm();
  fields.value.date_time.value = formatDate(new Date());
};

const allowNewItemRow = ref(false);
const handleVendor = (data: any) => {
  vendor.value = data;
  prepareItemRow();
};

const prepareItemRow = () => {
  if (
    !isEmpty(formData.value.vendors_id) &&
    !isEmpty(formData.value.warehouses_id) &&
    !isEmpty(formData.value.price_lists_id) &&
    !isEmpty(formData.value.credit_terms_id)
  ) {
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const activeTab = ref("items");

const updateItems = (items: Item[] | []) => {
  formData.value.items = items;
};

const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action, "Purhcase Debit Note# " + formData.value.ref_no);
};

const viewDocument = () => {
  emit('viewDocument', formData.value.invoice, 'Purchase Invoice', 'procurement/purchases/purchase-invoices')
}

const showForm = ref(false);
onMounted(() => {
  showForm.value = true;
  isReadOnly.value = props.mode == "view";      
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
  }
});


</script>

<template>
  <ModalFormComponent
      width="100%"
      @close="$emit('close')"
    >
      <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="false"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      >
      <template #dropdownbuttons>
          <button
            @click="viewDocument"
            v-if="formData.invoice && formData.invoice?.document_no"
            class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <IconComponent name="FileInput" />
            View Purchase Invoice ({{ formData.invoice?.document_no }})
          </button>
      </template>
    </DocumentHeader>
    </template>

      <!-- Main Content - Scrollable -->
      <template #form>
          <div class="grid md:grid-cols-2 gap-6 mb-8">
            <!-- Left Column -->
            <div class="space-y-6">
              <div>
                <div class="flex justify-between items-center mb-2">
                  <SearchSelect
                    label="Vendor"
                    end-point="procurement/master-data/vendors"
                    v-model="fields.vendors_id.value"
                    :readonly="isReadOnly"
                    :remote="true"
                    :withDetails="true"
                    :allow-new-record="true"
                    :error="
                      fields.vendors_id.touched ? fields.vendors_id.error : null
                    "
                    @blur="fields.vendors_id.touched = true"
                    @select="handleVendor"
                  />
                </div>

                <div
                  v-if="vendor"
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ vendor.name }}
                  </h4>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ vendor.email }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ vendor.mobile }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ vendor.address }}
                  </p>
                </div>
                <div
                  v-else
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 text-center"
                >
                  No Vendor selected
                </div>
              </div>
            </div>

            <!-- Right Column -->
            <div class="space-y-6">
              <!--  Details -->
              <div class="space-y-4">
                <div class="grid md:grid-cols-3 gap-4">
                  <div>
                    <SearchSelect
                      label="Warehouse"
                      end-point="system/company/warehouses"
                      v-model="fields.warehouses_id.value"
                      :readonly="isReadOnly"
                      @select="handleWarehouse"
                      :error="
                        fields.warehouses_id.touched
                          ? fields.warehouses_id.error
                          : null
                      "
                      @blur="fields.warehouses_id.touched = true"
                    />
                  </div>
                  <div>
                    <SearchSelect
                      label="Price List"
                      end-point="finance/master-data/price-lists"
                      v-model="fields.price_lists_id.value"
                      :readonly="isReadOnly"
                      @select="prepareItemRow"
                      :error="
                        fields.price_lists_id.touched
                          ? fields.price_lists_id.error
                          : null
                      "
                      @blur="fields.price_lists_id.touched = true"
                    />
                  </div>
                  <div>
                    <SearchSelect
                      label="Payment Terms"
                      end-point="finance/master-data/credit-terms"
                      v-model="fields.credit_terms_id.value"
                      :readonly="isReadOnly"
                      @select="prepareItemRow"
                      :error="
                        fields.credit_terms_id.touched
                          ? fields.credit_terms_id.error
                          : null
                      "
                      @blur="fields.credit_terms_id.touched = true"
                    />
                  </div>
                </div>

                <div class="grid md:grid-cols-3 gap-4">
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Date
                    </label>
                    <input
                      v-model="formData.date_time"
                      :readonly="isReadOnly"
                      type="date"
                      class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Vendor Reference Number
                    </label>
                    <input
                      v-model="fields.vendor_reference.value"
                      type="text"
                      :readonly="isReadOnly"
                      class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                  <div>
                    <label
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Document Number
                    </label>
                    <input
                      v-model="fields.ref_no.value"
                      type="text"
                      readonly
                      class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="mb-6"
            v-if="!isMobile"
          >
            <TabComponent
              :tabs="[
                { name: 'items', label: 'Items', visible: true },
                {
                  name: 'accounting',
                  label: 'Accounting',
                  visible: isReadOnly,
                },
              ]"
              v-model="activeTab"
            >
              <template #items>
                <!-- Items Table -->
                <ItemPurchaseGrid
                  :items="formData.items"
                  :readonly="isReadOnly"
                  :currency="formData.currency"
                  :exchange_rate="formData.exchange_rate"
                  :warehouses_id="formData.warehouses_id"
                  :price_lists_id="formData.price_lists_id"
                  :allow-new-row="allowNewItemRow"
                  :shelf-managed="warehouse?.shelf_managed ?? false"
                  :shelfs="warehouse?.shelfs"
                  @items="updateItems"
                />
                <!-- Terms and Notes -->
                <div class="grid md:grid-cols-2 gap-6">
                <div>
                  <div>
                    <TextAreaInput
                      label="Remarks"
                      v-model="formData.remarks"
                      :readonly="isReadOnly"
                      :rows="7"
                      placeholder="Additional notes or remarks"
                    ></TextAreaInput>
                  </div>
                </div>
                <div>
                  <TextAreaInput
                    label="Internal Notes"
                    v-model="formData.remarks"
                    :rows="7"
                    :readonly="isReadOnly"
                    placeholder="Internal Notes"
                  />
                </div>
              </div>
              </template>
              <template #accounting>
                <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
              </template>
            </TabComponent>
          </div>
          <template v-if="isMobile">
              <ItemPurchaseGrid
                ref="itemGridRef"
                :items="formData.items"
                :readonly="isReadOnly"
                :vendor="vendor"
                :currency="formData.currency"
                :exchange_rate="formData.exchange_rate"
                :warehouses_id="formData.warehouses_id"
                :price_lists_id="formData.price_lists_id"
                :allow-change-price="false"
                :allow-discount="true"
                :allow-new-row="allowNewItemRow"
                :shelf-managed="warehouse?.shelf_managed ?? false"
                :shelfs="warehouse?.shelfs"
                @items="updateItems"
              />
              <h3 class="mb-2" v-if="isReadOnly">Accounting</h3>
              <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
            </template>
        </template>
      <!-- Footer -->
      <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      />
      </template>
    </ModalFormComponent>
</template>
